import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MinusCircle, PlusCircle } from "@phosphor-icons/react"
import { Title } from "@radix-ui/react-dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { useDebounce } from "@uidotdev/usehooks"
import { ChartData } from "chart.js"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogPortal } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import {
  AvailableEmergencyPoolGlossary,
  AvailablePoolGlossary,
} from "@/modules/productivity-cockpit/components/capacity-glossary"
import { PlanningBarIndex } from "@/modules/productivity-cockpit/helpers/build-planning-chart"
import { UpdateCapacity } from "@/modules/productivity-cockpit/planning-chart"
import { useCapacityPlansQuery$data } from "@/modules/productivity-cockpit/services/__generated__/useCapacityPlansQuery.graphql"
import { useUpdateCapacityMutation } from "@/modules/productivity-cockpit/services/useUpdateCapacityMutation"

type CapacityUpdateDialogProps = {
  updateCapacity: UpdateCapacity
  setUpdateCapacity: Dispatch<SetStateAction<UpdateCapacity | null>>
  chartRef: React.RefObject<HTMLDivElement | null>
  chartData: ChartData
  data: useCapacityPlansQuery$data
}

const UPDATE_CAPACITY_DIALOG_POSITION_LIMITS = {
  MIN: 150,
  MAX: 1000,
}

const CapacityUpdateDialog = ({
  updateCapacity,
  setUpdateCapacity,
  chartRef,
  chartData,
  data,
}: CapacityUpdateDialogProps) => {
  // this ref is used to prevent the useEffect from calling the mutation on the first render
  // and selecting other bar
  const callMutation = useRef(false)
  const { t } = useTranslation()

  const [updatedCapacity, setUpdatedCapacity] = useState(0)
  const debouncedUpdatedCapacity = useDebounce(updatedCapacity, 500)

  const updateCapacityMutation = useUpdateCapacityMutation()
  const { datasetIndex, index, clientX, clientY } = updateCapacity

  const currentDataset = chartData.datasets[datasetIndex]
  const { labels } = chartData

  const {
    date,
    slotId,
    additionalReservedCapacity,
    additionalPoolCapacity,
    plannedPoolCapacity,
    reservedCapacity,
    initialReservedCapacity,
    poolCapacity,
    overbookedEmergencyCapacity,
    bookedPoolCapacity,
    availablePoolCapacity,
    availableEmergencyCapacity,
    plannedEmergencyCapacity,
    bookedEmergencyCapacity,
  } = data.capacityPlans[index]

  const getCurrentCapacityValue = () => {
    switch (datasetIndex) {
      case PlanningBarIndex.AVAILABLE_CAPACITY:
        return plannedPoolCapacity - reservedCapacity
      case PlanningBarIndex.AVAILABLE_EMERGENCY_CAPACITY:
        return initialReservedCapacity
      default:
        return 0
    }
  }

  const getCapacityValueText = () => {
    switch (datasetIndex) {
      case PlanningBarIndex.AVAILABLE_CAPACITY:
        // Available = (poolCapacity - bookedPoolCapacity - reservedCapacity - overbookedEmergencyCapacity) + |additionalPoolCapacity| = value
        return (
          <AvailablePoolGlossary
            {...{
              additionalPoolCapacity,
              availablePoolCapacity,
              bookedPoolCapacity,
              label: currentDataset.label,
              overbookedEmergencyCapacity,
              poolCapacity,
              reservedCapacity,
            }}
          />
        )
      case PlanningBarIndex.AVAILABLE_EMERGENCY_CAPACITY:
        // AvailableEmergencyCapacity = (reservedCapacity + plannedEmergencyCapacity - bookedEmergencyCapacity) + |additionalReservedCapacity| = value
        return (
          <AvailableEmergencyPoolGlossary
            {...{
              additionalReservedCapacity,
              availableEmergencyCapacity,
              bookedEmergencyCapacity,
              plannedEmergencyCapacity,
              reservedCapacity,
              label: currentDataset.label,
            }}
          />
        )
      default:
        return ""
    }
  }

  const handleUpdateCapacity = (value: number) => {
    callMutation.current = true
    setUpdatedCapacity(value)
  }

  useEffect(() => {
    if (callMutation.current && datasetIndex === PlanningBarIndex.AVAILABLE_CAPACITY) {
      updateCapacityMutation({
        date,
        slotId,
        additionalReservedCapacity,
        additionalPoolCapacity: debouncedUpdatedCapacity,
      })
      callMutation.current = false
    }
  }, [debouncedUpdatedCapacity, updateCapacityMutation, datasetIndex, date, slotId, additionalReservedCapacity])

  useEffect(() => {
    if (callMutation.current && datasetIndex === PlanningBarIndex.AVAILABLE_EMERGENCY_CAPACITY) {
      updateCapacityMutation({
        date,
        slotId,
        additionalPoolCapacity,
        additionalReservedCapacity: debouncedUpdatedCapacity,
      })
      callMutation.current = false
    }
  }, [debouncedUpdatedCapacity, updateCapacityMutation, datasetIndex, date, slotId, additionalPoolCapacity])

  useEffect(() => {
    switch (datasetIndex) {
      case PlanningBarIndex.AVAILABLE_CAPACITY:
        setUpdatedCapacity(additionalPoolCapacity)
        break
      case PlanningBarIndex.AVAILABLE_EMERGENCY_CAPACITY:
        setUpdatedCapacity(additionalReservedCapacity)
        break
    }
    callMutation.current = false
  }, [additionalPoolCapacity, setUpdatedCapacity, additionalReservedCapacity, datasetIndex])

  return (
    <Dialog
      modal={false}
      open={!!updateCapacity}
      onOpenChange={() => {
        setUpdateCapacity(null)
      }}>
      <DialogPortal container={chartRef.current}>
        <VisuallyHidden asChild>
          <Title>Hidden Title</Title>
        </VisuallyHidden>
        <DialogContent
          onMouseDown={(e) => e.stopPropagation()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          variant="small"
          data-testid="update-capacity-dialog-content"
          style={{ left: clientX, top: clientY }}
          className={cn(
            "absolute min-h-16 min-w-75 translate-y-28",
            clientX < UPDATE_CAPACITY_DIALOG_POSITION_LIMITS.MIN
              ? "translate-x-0"
              : clientX > UPDATE_CAPACITY_DIALOG_POSITION_LIMITS.MAX
                ? "translate-x-[-90%]"
                : "translate-x-[-40%]",
          )}>
          <div className="flex items-center justify-between gap-2 px-4 py-3">
            <div className="flex flex-col">
              <div className="text-on-surface">{getCapacityValueText()}</div>
              {getCurrentCapacityValue() + updatedCapacity < 0 && (
                <div className="text-on-surface">
                  {`(${t("scheduling.productivityCockpit.planningChart.capacityCannotBeNegative")})`}
                </div>
              )}
              <div className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]">
                {labels
                  ? labels.length - 1 !== index
                    ? `${labels.at(index)} - ${labels.at(index + 1)}`
                    : `${labels.at(index)} - ${labels.at(0)}`
                  : ""}
              </div>
            </div>
            <div className="flex gap-1">
              <Button
                variant="circle"
                className="h-10 w-10 p-0"
                onClick={() => handleUpdateCapacity(updatedCapacity - 1)}>
                <MinusCircle />
              </Button>
              <Button
                variant="circle"
                className="h-10 w-10 p-0"
                onClick={() => handleUpdateCapacity(updatedCapacity + 1)}>
                <PlusCircle />
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
export { CapacityUpdateDialog }
